import { startOfDay } from "date-fns";
import { addDays } from "date-fns/addDays";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { proxy, subscribe } from "valtio";

import { authState } from "@/app/state/authState";
import { LocalStorage } from "@/app/utils/localStorage";

/**
 * Compute start-of-day in the org's timezone, returned as a UTC Date.
 *
 * The browser's `new Date()` gives "now" in the user's local timezone.
 * `startOfDay(new Date())` computes midnight in the *browser's* timezone,
 * which when serialized to UTC can land on the wrong calendar date for
 * users whose browser timezone differs from the org's timezone.
 *
 * Fix: project "now" into the org's timezone, compute midnight there,
 * then convert back to a real UTC timestamp.
 */
function startOfOrgDay(): Date {
  const tz = authState.auth.backend.user?.org?.tz;
  if (!tz) {
    return startOfDay(new Date());
  }
  const nowInOrgTz = toZonedTime(new Date(), tz);
  return fromZonedTime(startOfDay(nowInOrgTz), tz);
}

export namespace inboxState {
  const storage = new LocalStorage("inboxFilter");

  export const inboxFilter = proxy<{ dateKey: InboxFilterOption }>(
    storage.get({ fallback: { dateKey: "today" } }), // default to only today
  );

  export const actions = proxy({
    count: null as number | null,
  });

  subscribe(inboxFilter, () => {
    storage.save(inboxFilter);
  });

  export const dates = {
    get today() {
      return startOfOrgDay();
    },
    get days3() {
      return addDays(startOfOrgDay(), 3);
    },
    get week() {
      return addDays(startOfOrgDay(), 7);
    },
  };

  export const dateLabels: Record<InboxFilterOption, string> = {
    today: "Today",
    days3: "3 Days",
    week: "7 Days",
  };

  export type InboxFilterOption = keyof typeof inboxState.dates;
}
